import { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema, ArticleSchema, FAQSchema } from '@/components/feature/StructuredData';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return <Navigate to="/" replace />;
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 6);

  const visibleChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 20);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${comic.title} - 韩漫免费在线阅读全集 - 韩国漫画网`}
        description={`${comic.title}：${comic.description} 作者${comic.author}，共${comic.totalChapters}话，${comic.status}。韩国漫画网为您提供${comic.title}全集免费在线阅读。`}
        keywords={`${comic.title},${comic.title}免费阅读,${comic.category}韩漫,韩国漫画网`}
        canonicalUrl={`https://www.koreacomic.cn/comic/${comic.id}`}
        ogType="article"
      />
      <WebPageSchema
        title={`${comic.title} - 韩国漫画网`}
        description={comic.description}
        url={`https://www.koreacomic.cn/comic/${comic.id}`}
        datePublished="2024-01-15"
        dateModified={comic.updateDate}
      />
      <ArticleSchema
        id={comic.id}
        title={comic.title}
        description={comic.longDescription}
        author={comic.author}
        datePublished="2024-01-15"
        dateModified={comic.updateDate}
        imageUrl={comic.coverImage}
        category={comic.category}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Comic Info */}
            <article className="mt-6">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Cover */}
                <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px]">
                  <div className="rounded-lg overflow-hidden bg-background-200 aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画网`}
                      title={`${comic.title} - 韩漫免费阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Details */}
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {comic.title}
                  </h1>

                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">分类：{comic.category}</span>
                    <span className="text-foreground-300">|</span>
                    <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-500' : 'text-secondary-500'}`}>
                      {comic.status}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">共{comic.totalChapters}话</span>
                  </div>

                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                      <span className="text-lg font-bold text-accent-500">{comic.rating}</span>
                      <span className="text-xs text-foreground-500">分</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-eye-line text-sm"></i>
                      <span className="text-sm">{(comic.views / 10000).toFixed(1)}万次阅读</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.longDescription}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 mt-6 flex-wrap">
                    <button
                      onClick={() => {
                        const firstChapter = comic.chapters[comic.chapters.length - 1];
                        setSelectedChapter(firstChapter.number);
                      }}
                      className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line"></i>
                      开始阅读
                    </button>
                    <button
                      onClick={() => setSelectedChapter(comic.chapters[0].number)}
                      className="inline-flex items-center gap-2 bg-background-200 hover:bg-background-300 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-skip-forward-line"></i>
                      阅读最新章节
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Chapter List */}
            <section className="mt-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-heading font-bold text-foreground-900">
                  章节列表
                </h2>
                <span className="text-sm text-foreground-500">
                  共 {comic.totalChapters} 话
                </span>
              </div>

              <div className="bg-background-100 rounded-lg p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {visibleChapters.map((ch) => (
                    <button
                      key={ch.id}
                      onClick={() => setSelectedChapter(ch.number)}
                      className={`text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        selectedChapter === ch.number
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      {ch.title}
                    </button>
                  ))}
                </div>
                {comic.totalChapters > 20 && (
                  <button
                    onClick={() => setShowAllChapters(!showAllChapters)}
                    className="mt-3 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {showAllChapters ? '收起' : `展开全部 ${comic.totalChapters} 话`}
                  </button>
                )}
              </div>
            </section>

            {/* Reading Modal */}
            {selectedChapter !== null && (
              <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
                <div className="bg-background-50 rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-4 border-b border-background-200">
                    <h3 className="font-medium text-foreground-900">
                      {comic.title} - 第{selectedChapter}话
                    </h3>
                    <button
                      onClick={() => setSelectedChapter(null)}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors cursor-pointer"
                      aria-label="关闭"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                  <div className="p-6 text-center">
                    <i className="ri-book-open-line text-5xl text-primary-300 mb-4 block"></i>
                    <p className="text-foreground-900 font-medium text-lg mb-2">第{selectedChapter}话</p>
                    <p className="text-foreground-500 text-sm mb-6">点击下方按钮开始阅读</p>
                    <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-play-circle-line"></i>
                      进入阅读模式
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-4 border-t border-background-200">
                    <button
                      onClick={() => {
                        const prev = comic.chapters.find((c) => c.number === selectedChapter - 1);
                        if (prev) setSelectedChapter(prev.number);
                      }}
                      disabled={selectedChapter <= 1}
                      className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-1"></i>上一话
                    </button>
                    <span className="text-sm text-foreground-500">{selectedChapter} / {comic.totalChapters}</span>
                    <button
                      onClick={() => {
                        const next = comic.chapters.find((c) => c.number === selectedChapter + 1);
                        if (next) setSelectedChapter(next.number);
                      }}
                      disabled={selectedChapter >= comic.totalChapters}
                      className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      下一话<i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="max-w-screen-2xl mx-auto px-4 md:px-6 pb-14" data-product-shop>
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-lg font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
                <span className="w-8 h-0.5 bg-accent-500 rounded-full mt-1"></span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="mt-2">
                      <h3 className="text-xs font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                        {rc.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{rc.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* FAQ */}
          <section className="bg-background-100/70 py-14">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                常见问题
              </h2>
              <FAQSchema />
              <div className="max-w-3xl mx-auto grid gap-3">
                {[
                  {
                    q: '这部漫画多久更新一次？',
                    a: `《${comic.title}》的更新频率视具体情况而定。${comic.status === '连载中' ? '目前处于连载中，通常每周更新一到两话。' : '本作已完结，您可以一次性阅读全部章节。'}`,
                  },
                  {
                    q: '可以在手机上阅读这部漫画吗？',
                    a: '当然可以！韩国漫画网针对移动设备进行了全面优化，您可以在手机、平板等任何设备上流畅阅读所有漫画。',
                  },
                  {
                    q: '这部漫画是免费的吗？',
                    a: `是的！《${comic.title}》在韩国漫画网上完全免费，无需付费即可阅读全部章节，无任何隐藏收费。`,
                  },
                  {
                    q: '如何收藏这部漫画？',
                    a: '您可以将本页面添加到浏览器书签，方便下次继续阅读。我们正在开发收藏功能，敬请期待。',
                  },
                ].map((faq, idx) => (
                  <details key={idx} className="bg-background-50 rounded-lg p-4 cursor-pointer group">
                    <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                      {faq.q}
                      <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                    </summary>
                    <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}